import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PublicLayout } from "@/components/PublicLayout";
import { useI18n } from "@/i18n/I18nContext";
import { useUniverse } from "@/context/UniverseContext";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import api from "@/lib/api";
import { ArrowRight, Check } from "lucide-react";

const IMG = {
  fitness: "https://images.unsplash.com/photo-1548690312-e3b507d8c110?w=1600&q=80",
  pilates: "https://images.unsplash.com/photo-1717500252573-d31d4bf5ddf1?w=1600&q=80",
};

const SEO = {
  fitness: {
    title: "Frenchies Fitness — Gym, Sport & Fitness en Coronado, Panamá",
    description: "Gym, sport y fitness en Coronado, Panamá: fitness funcional, CrossFit, calistenia y coaching premium. Entrena con los mejores coaches. Reserva tu clase hoy.",
    keywords: "Gym Coronado, Sport Coronado, Fitness Coronado, CrossFit Coronado, gimnasio Coronado, deporte Coronado, Coaching Premium, gimnasio Panamá",
  },
  pilates: {
    title: "Frenchies Pilates — Pilates Reformer & Clases Privadas en Coronado",
    description: "Pilates Reformer, clases grupales y privadas en Coronado, Panamá. Mejora tu postura, fuerza y energía. Reserva tu clase hoy.",
    keywords: "Pilates Coronado, Pilates Reformer, Sport Coronado, Fitness Coronado, Private Classes, clases privadas, Pilates Panamá",
  },
};

export default function UniversePage({ universe }) {
  const { t, tl } = useI18n();
  const { setUniverse } = useUniverse();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);

  useEffect(() => {
    setUniverse(universe);
    api.get(`/services?universe=${universe}`).then(({ data }) => setServices(data)).catch(() => {});
  }, [universe, setUniverse]);

  const title = universe === "fitness" ? "Frenchies Fitness" : "Frenchies Pilates";

  return (
    <PublicLayout>
      <Seo title={SEO[universe].title} description={SEO[universe].description} path={`/${universe}`} keywords={SEO[universe].keywords} />
      <section className="relative h-[60vh] min-h-[420px] w-full overflow-hidden">
        <img src={IMG[universe]} alt={title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-5 pb-16 lg:px-8">
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="font-display text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
            {title}
          </motion.h1>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <h2 className="mb-8 font-display text-3xl font-bold tracking-tight">{t("home.ourServices")}</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => (
            <motion.div key={s.service_id} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.03 }}
              className="rounded-2xl border border-border bg-card p-6">
              <Check className="mb-3 h-6 w-6 text-primary" />
              <h3 className="font-display text-lg font-semibold">{tl(s.name)}</h3>
            </motion.div>
          ))}
        </div>
        <div className="mt-12 flex flex-wrap gap-3">
          <Button size="lg" data-testid="universe-schedule-btn" onClick={() => navigate("/schedule")} className="rounded-full">
            {t("nav.schedule")} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button size="lg" variant="outline" data-testid="universe-pricing-btn" onClick={() => navigate("/pricing")} className="rounded-full">
            {t("nav.pricing")}
          </Button>
        </div>
      </section>
    </PublicLayout>
  );
}
